import { useEffect, useState } from "react";
import { Calendar, Loader2, Plus, Save, Power, Archive, Trash2, AlertTriangle, Download } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { api } from "@/lib/api";
import type { Event } from "@/types/database";

export default function AdminEventsPage() {
  const [rows, setRows] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<Event> | null>(null);
  const [editing, setEditing] = useState<Record<string, Partial<Event>>>({});

  async function load() {
    setLoading(true);
    setError(null);
    const { data, error } = await supabase
      .from("events")
      .select("*")
      .order("active", { ascending: false })
      .order("starts_at", { ascending: false });
    if (error) setError(error.message);
    setRows((data ?? []) as Event[]);
    setLoading(false);
  }
  useEffect(() => { void load(); }, []);

  async function setActive(id: string) {
    setBusyId(id);
    setError(null);
    // Deactivate any others first (the partial-unique index would block otherwise)
    await supabase.from("events").update({ active: false }).neq("id", id);
    const { error } = await supabase
      .from("events")
      .update({ active: true, archived: false, updated_at: new Date().toISOString() })
      .eq("id", id);
    if (error) setError(error.message);
    setBusyId(null);
    await load();
  }

  async function archiveEvent(id: string) {
    setBusyId(id);
    setError(null);
    const { error } = await supabase
      .from("events")
      .update({ active: false, archived: true, updated_at: new Date().toISOString() })
      .eq("id", id);
    if (error) setError(error.message);
    setBusyId(null);
    await load();
  }

  async function exportEventCsv(id: string, name: string) {
    setBusyId(id);
    try {
      const res = await api.adminExportCsv("event_leaderboard", { event_id: id });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name.replace(/[^A-Za-z0-9._-]/g, "_")}-leaderboard.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not export CSV.");
    }
    setBusyId(null);
  }

  async function removeEvent(id: string) {
    if (!confirm("Permanently delete this event? Game sessions linked to it will keep their event_id but the event row will be gone (their leaderboard entries will move to the 'no event' bucket).")) return;
    setBusyId(id);
    const { error } = await supabase.from("events").delete().eq("id", id);
    if (error) setError(error.message);
    setBusyId(null);
    await load();
  }

  function update(id: string, key: keyof Event, val: unknown) {
    setEditing((prev) => ({ ...prev, [id]: { ...prev[id], [key]: val } }));
  }

  async function saveEdit(id: string) {
    const patch = editing[id];
    if (!patch) return;
    setBusyId(id);
    setError(null);
    const { error } = await supabase
      .from("events")
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq("id", id);
    if (error) setError(error.message);
    setEditing((prev) => { const n = { ...prev }; delete n[id]; return n; });
    setBusyId(null);
    await load();
  }

  async function createDraft() {
    if (!draft?.name?.trim()) {
      setError("Event name is required.");
      return;
    }
    setError(null);
    const { error } = await supabase.from("events").insert({
      name: draft.name.trim(),
      location: draft.location || null,
      city: draft.city || null,
      starts_at: draft.starts_at || null,
      ends_at: draft.ends_at || null,
      active: false,
      archived: false,
    });
    if (error) setError(error.message);
    setDraft(null);
    await load();
  }

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl text-white">Events</h1>
          <p className="text-steel-300">
            Tag each game session with a contest. Only one event is active at a time —
            the active event auto-applies to new students that register and play.
          </p>
        </div>
        <button onClick={() => setDraft({ name: "", location: "", city: "" })} className="btn-primary">
          <Plus className="w-4 h-4" /> New event
        </button>
      </header>

      {error && (
        <div className="pill-bad px-3 py-2 inline-flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {draft && (
        <div className="card-dark p-4 space-y-2">
          <div className="text-sm uppercase tracking-wider text-steel-300">New event</div>
          <div className="grid sm:grid-cols-2 gap-2">
            <input className="input" placeholder="Event name (e.g. Houston HCC Spring Welding Contest)" value={draft.name ?? ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            <input className="input" placeholder="Location (e.g. Houston Community College – Welding Lab)" value={draft.location ?? ""} onChange={(e) => setDraft({ ...draft, location: e.target.value })} />
            <input className="input" placeholder="City (e.g. Houston)" value={draft.city ?? ""} onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <input className="input" type="datetime-local" value={draft.starts_at ?? ""} onChange={(e) => setDraft({ ...draft, starts_at: e.target.value })} title="Starts at" />
              <input className="input" type="datetime-local" value={draft.ends_at ?? ""} onChange={(e) => setDraft({ ...draft, ends_at: e.target.value })} title="Ends at" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setDraft(null)} className="btn-outline">Cancel</button>
            <button onClick={createDraft} className="btn-primary"><Save className="w-4 h-4" /> Save</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-steel-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card-dark p-6 text-center text-steel-300">
          No events yet. Click <span className="text-forge-300">New event</span> to create one.
        </div>
      ) : (
        <div className="card-dark overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-steel-900/60 text-steel-300 uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Location · City</th>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-steel-700">
              {rows.map((e) => {
                const eff = <K extends keyof Event>(k: K): Event[K] | undefined =>
                  (editing[e.id]?.[k] as Event[K] | undefined) ?? e[k];
                const dirty = !!editing[e.id];
                return (
                  <tr key={e.id} className={e.active ? "bg-forge-500/5" : ""}>
                    <td className="p-2">
                      <input className="input py-1" value={String(eff("name") ?? "")} onChange={(ev) => update(e.id, "name", ev.target.value)} />
                    </td>
                    <td className="p-2">
                      <div className="grid gap-1">
                        <input className="input py-1 text-xs" placeholder="Location" value={String(eff("location") ?? "")} onChange={(ev) => update(e.id, "location", ev.target.value)} />
                        <input className="input py-1 text-xs" placeholder="City" value={String(eff("city") ?? "")} onChange={(ev) => update(e.id, "city", ev.target.value)} />
                      </div>
                    </td>
                    <td className="p-2">
                      <div className="grid gap-1">
                        <input className="input py-1 text-xs" type="datetime-local" value={toLocalInput(eff("starts_at"))} onChange={(ev) => update(e.id, "starts_at", ev.target.value || null)} title="Starts at" />
                        <input className="input py-1 text-xs" type="datetime-local" value={toLocalInput(eff("ends_at"))} onChange={(ev) => update(e.id, "ends_at", ev.target.value || null)} title="Ends at" />
                      </div>
                    </td>
                    <td className="p-2">
                      {e.archived ? <span className="pill-bad">Archived</span> :
                        e.active ? <span className="pill-ok">Active</span> :
                        <span className="pill-warn">Draft</span>}
                    </td>
                    <td className="p-2 text-right whitespace-nowrap space-x-2">
                      {dirty && <button onClick={() => saveEdit(e.id)} disabled={busyId === e.id} className="text-forge-300 hover:underline inline-flex items-center gap-1"><Save className="w-4 h-4" />Save</button>}
                      {!e.active && !e.archived && (
                        <button onClick={() => setActive(e.id)} disabled={busyId === e.id} className="btn-outline">
                          <Power className="w-4 h-4" /> Activate
                        </button>
                      )}
                      {!e.archived && (
                        <button onClick={() => archiveEvent(e.id)} disabled={busyId === e.id} className="btn-outline">
                          <Archive className="w-4 h-4" /> Archive
                        </button>
                      )}
                      <button onClick={() => exportEventCsv(e.id, e.name)} disabled={busyId === e.id} className="btn-outline">
                        <Download className="w-4 h-4" /> CSV
                      </button>
                      <button onClick={() => removeEvent(e.id)} disabled={busyId === e.id} className="text-rose-400 hover:underline inline-flex items-center gap-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-steel-400 inline-flex items-center gap-1">
        <Calendar className="w-3 h-3" /> The active event auto-tags new game sessions. Switching activates the new one and deactivates the old.
      </p>
    </div>
  );
}

function toLocalInput(v: unknown): string {
  if (!v || typeof v !== "string") return "";
  // Trim "Z" or seconds for the datetime-local input
  // ISO timestamp -> 'YYYY-MM-DDTHH:MM'
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
